import { useMemo } from "react";
import { Box } from "@mui/material";
import { Button, Chip, DataTableV2, Typography } from "@platform/design-system";

import { RepStatusChip } from "../components";
import { REP_TYPE_LABELS } from "../constants";

import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { CarrierRep } from "../types";

const ROW_HEIGHT = 64;
const EMPTY_VALUE = "—";
const INACTIVE_ROW_STYLE = { opacity: 0.5 };

type CarrierRepCellProps = ICellRendererParams<CarrierRep>;

const TwoLineCell = ({
  primary,
  secondary,
}: {
  primary: string;
  secondary: string | null;
}): React.JSX.Element => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    height="100%"
  >
    <Typography variant="body-medium" component="span">
      {primary}
    </Typography>
    {secondary && (
      <Typography variant="caption" component="span" color="textSecondary">
        {secondary}
      </Typography>
    )}
  </Box>
);

const NameCell = ({ data }: CarrierRepCellProps): React.JSX.Element => (
  <TwoLineCell primary={data!.name ?? EMPTY_VALUE} secondary={data!.title} />
);

const ContactCell = ({ data }: CarrierRepCellProps): React.JSX.Element => (
  <TwoLineCell primary={data!.email} secondary={data!.phone} />
);

const RoleCell = ({ data }: CarrierRepCellProps): React.JSX.Element => {
  const label = REP_TYPE_LABELS[data!.type];

  return data!.type === "mailbox" ? (
    <Chip label={label} size="small" leadingVisual={false} />
  ) : (
    <Typography variant="body-medium" component="span">
      {label}
    </Typography>
  );
};

const StatusCell = ({ data }: CarrierRepCellProps): React.JSX.Element => (
  <RepStatusChip active={data!.active} />
);

const BrokersCell = ({ data }: CarrierRepCellProps): React.JSX.Element => (
  <Box
    display="flex"
    flexWrap="wrap"
    alignItems="center"
    gap={0.5}
    height="100%"
  >
    {data!.brokers.map(({ id, name }) => (
      <Chip key={id} label={name} size="small" leadingVisual={false} />
    ))}
  </Box>
);

const EditCell = ({
  rep,
  onEditRep,
}: {
  rep: CarrierRep;
  onEditRep: (rep: CarrierRep) => void;
}): React.JSX.Element => (
  <Button
    variant="secondary"
    size="sm"
    data-testid={`edit-rep-button-${rep.id}`}
    disabled={!rep.active}
    onClick={() => onEditRep(rep)}
  >
    Edit
  </Button>
);

interface CarrierRepsTableProps {
  reps: CarrierRep[];
  onEditRep: (rep: CarrierRep) => void;
}

export const CarrierRepsTable = ({
  reps,
  onEditRep,
}: CarrierRepsTableProps): React.JSX.Element => {
  const columnDefs = useMemo<ColDef<CarrierRep>[]>(
    () => [
      { colId: "name", headerName: "Name", cellRenderer: NameCell },
      {
        colId: "email",
        headerName: "Email",
        flex: 1.5,
        cellRenderer: ContactCell,
      },
      { colId: "role", headerName: "Role", flex: 0.75, cellRenderer: RoleCell },
      {
        colId: "status",
        headerName: "Status",
        flex: 0.75,
        cellRenderer: StatusCell,
      },
      {
        colId: "brokers",
        headerName: "Brokers",
        flex: 1.5,
        cellRenderer: BrokersCell,
      },
      {
        colId: "actions",
        headerName: "",
        maxWidth: 100,
        cellRenderer: ({ data }: CarrierRepCellProps) => (
          <EditCell rep={data!} onEditRep={onEditRep} />
        ),
      },
    ],
    [onEditRep],
  );

  return (
    <Box data-testid="carrier-reps-table">
      <DataTableV2
        configure={{ preset: "minimal" }}
        rowData={reps}
        columnDefs={columnDefs}
        rowHeight={ROW_HEIGHT}
        getRowId={({ data }) => String(data.id)}
        getRowStyle={({ data }) =>
          data?.active === false ? INACTIVE_ROW_STYLE : undefined
        }
      />
    </Box>
  );
};
